// ── Resource download section — role/category-gated + 503 gate-closed (Wave 8) ─
// CONTRACT CHECK (verified against vjal-coaching-api): the resource download
// operation returns a HARDCODED HTTP 503 (R2 closed). So even when a download is
// IN SCOPE for the role/category, the transfer is a GATE-CLOSED honest state and
// the control fires NOTHING (no fake file / signed URL).
//
// ── REFERENCE-VIDEO DOWNLOAD restriction (Wave 8 contract check) ─────────────
// Coach may VIEW reference videos but may NOT download them. canRoleDownloadResource
// governs whether the download is even OFFERED:
//   - STAFF download any category;
//   - coach / company_admin / cohort_lead: reference videos are VIEW-only → no
//     download affordance (an honest "view-only" note instead of a button);
//   - documentation / program_resource: the download is offered but is the 503
//     gate-closed honest state (no real transfer yet).
// So a coach NEVER even sees a reference-video download control. The biting test
// asserts the ABSENCE of the download affordance on a reference video for coach.

import { Alert, Button, Module, ModuleBody, ModuleHead } from "@vjal/ui";
import { Download } from "lucide-react";

import type { CoachingResourceDto } from "../../../services/coaching-dtos";
import type { CoachingRole } from "../../../domain/roles";
import { canRoleDownloadResource, isReferenceVideo } from "../resources-helpers";

export function ResourceDownloadSection({ resource, role }: { resource: CoachingResourceDto; role: CoachingRole }) {
  const canDownload = canRoleDownloadResource(role, resource);
  const referenceVideo = isReferenceVideo(resource);

  return (
    <Module span={4}>
      <ModuleHead title="Download" subtitle="File download follows your role and the resource category." />
      <ModuleBody className="space-y-3" data-testid="resource-download">
        {!canDownload ? (
          // Reference video + non-staff role → VIEW-only. No download is offered.
          <div data-honest-posture="permission-denied">
            <Alert variant="info">
              {referenceVideo
                ? "Reference videos are view-only for your role — there's no download. Open the resource link above to watch it."
                : "This resource isn't available to download in your role."}
            </Alert>
          </div>
        ) : (
          <div data-honest-posture="gate-closed" className="space-y-3">
            <Alert variant="warning">
              File download isn't available yet — file delivery is being connected. Nothing was downloaded. The resource's
              link is available above.
            </Alert>
            {/* Disabled by design: the download endpoint returns 503 (R2 closed),
                so the control fires NOTHING. It never produces a fake file/URL. */}
            <Button type="button" variant="secondary" leftIcon={<Download size={16} />} disabled aria-label="Download resource">
              Download not available yet
            </Button>
          </div>
        )}
      </ModuleBody>
    </Module>
  );
}
